import React, { useState, useEffect } from 'react';

const AddPageModal = ({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) => {
  // Steps: 1 = Connect, 2 = Select Pages, 3 = Saving, 4 = Success
  const [step, setStep] = useState(1);
  const [availablePages, setAvailablePages] = useState<{ id: string; name: string; category: string; img: string }[]>([]);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedPages([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // STEP 1: Simulate the Facebook OAuth process
  const handleConnect = async () => {
    // In a real app, this might open a popup using the Facebook JS SDK, 
    // or redirect to your /api/auth route and use the callback.html you built.
    // For this UI, we will simulate fetching the pages after a successful auth.
    setStep(3); // Temporary loading state
    
    setTimeout(() => {
      // Mock data representing pages fetched from FB Graph API
      setAvailablePages([
        { id: '1001', name: 'Scryptworx Main', category: 'Software Company', img: 'https://ui-avatars.com/api/?name=SM&background=F6B34B&color=19181c' },
        { id: '1002', name: 'Scryptworx Support', category: 'Customer Service', img: 'https://ui-avatars.com/api/?name=SS&background=F6B34B&color=19181c' },
        { id: '1003', name: 'Personal Blog', category: 'Personal', img: 'https://ui-avatars.com/api/?name=PB&background=2ecc71&color=fff' },
      ]);
      setStep(2);
    }, 1500);
  };

  const togglePageSelection = (pageId: string) => {
    setSelectedPages(prev => 
      prev.includes(pageId) 
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  // STEP 3: Send selected pages to your backend to save Page Access Tokens
  const handleSaveToServer = async () => {
    if (selectedPages.length === 0) return;
    
    setStep(3);

    try {
      // TODO: POST `selectedPages` and the User Access Token to your proxy server.
      // Your server will exchange the User Token for Page Tokens and save them to the DB.
      /*
      await fetch('/api/save-fb-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages: selectedPages })
      });
      */
      
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate network
      setStep(4);

      // Tell the parent component to refresh its page list
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);

    } catch (error) {
      console.error("Failed to save pages to server");
      setStep(2); // Revert to selection on error
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      
      <div className="bg-[#19181c] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-[#F6B34B]">Connect Facebook Pages</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto">
          
          {/* STEP 1: Connect Account */}
          {step === 1 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#1877F2]/10 text-[#1877F2] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fa-brands fa-facebook-f text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Authorize Meta Manager</h3>
              <p className="text-white/60 text-sm mb-8 px-4">
                To add new pages, you need to grant this application permission to view and manage your Facebook Pages.
              </p>
              <button 
                onClick={handleConnect}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-bold text-white bg-[#1877F2] hover:bg-[#1877F2]/90 transition-colors shadow-lg shadow-[#1877F2]/20"
              >
                <i className="fa-brands fa-facebook mr-2 text-lg"></i>
                Continue with Facebook
              </button>
            </div>
          )}

          {/* STEP 2: Select Pages */}
          {step === 2 && (
            <div className="flex flex-col h-full">
              <p className="text-white/70 text-sm mb-4">
                Select the pages you want to import to the server for automated publishing.
              </p>
              
              <div className="space-y-3 mb-6">
                {availablePages.map(page => (
                  <label 
                    key={page.id} 
                    className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedPages.includes(page.id) 
                        ? 'border-[#F6B34B] bg-[#F6B34B]/10' 
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="relative flex-shrink-0 w-12 h-12 rounded-full overflow-hidden mr-4">
                      <img src={page.img} alt={page.name} className="object-cover w-full h-full" />
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-white font-medium">{page.name}</h4>
                      <p className="text-white/50 text-xs">{page.category}</p>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${
                        selectedPages.includes(page.id) ? 'bg-[#F6B34B] border-[#F6B34B]' : 'border-white/30'
                      }`}>
                        {selectedPages.includes(page.id) && (
                          <svg className="w-4 h-4 text-[#19181c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                          </svg>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-auto flex gap-3 pt-4 border-t border-white/10">
                <button onClick={onClose} className="flex-1 py-3 px-4 rounded-lg text-sm font-bold text-white bg-white/10 hover:bg-white/20 transition-colors">
                  CANCEL
                </button>
                <button 
                  onClick={handleSaveToServer}
                  disabled={selectedPages.length === 0}
                  className="flex-1 py-3 px-4 rounded-lg text-sm font-bold text-[#19181c] bg-[#F6B34B] hover:bg-[#F6B34B]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  IMPORT {selectedPages.length > 0 ? `(${selectedPages.length})` : ''}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Loading / Saving State */}
          {step === 3 && (
            <div className="text-center py-12">
              <svg className="animate-spin w-12 h-12 text-[#F6B34B] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h3 className="text-lg font-medium text-white">Communicating with Server...</h3>
              <p className="text-white/50 text-sm mt-2">Generating permanent access tokens.</p>
            </div>
          )}

          {/* STEP 4: Success */}
          {step === 4 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#2ecc71]/20 text-[#2ecc71] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Pages Added Successfully!</h3>
              <p className="text-white/60 text-sm">You can now publish to these pages via the manager.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AddPageModal;